
import React from 'react';

const CertificationsSection: React.FC = () => {
  const certifications = [
    {
      name: 'Joint Commission International',
      description: 'Gold Seal of Approval for Patient Safety and Quality',
      icon: 'ri-shield-check-line',
      year: '2023',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      name: 'ISO 9001:2015',
      description: 'Quality Management System Certification',
      icon: 'ri-award-line',
      year: '2023',
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      name: 'HIMSS Stage 7',
      description: 'Electronic Medical Record Adoption Model',
      icon: 'ri-computer-line',
      year: '2022',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      name: 'Magnet Recognition',
      description: 'Excellence in Nursing Services',
      icon: 'ri-heart-pulse-line',
      year: '2023',
      color: 'text-red-600',
      bgColor: 'bg-red-100'
    },
    {
      name: 'AAAHC Accreditation',
      description: 'Ambulatory Health Care Excellence',
      icon: 'ri-hospital-line',
      year: '2023',
      color: 'text-teal-600',
      bgColor: 'bg-teal-100'
    },
    {
      name: 'CARF Accreditation',
      description: 'Rehabilitation Services Excellence',
      icon: 'ri-wheelchair-line',
      year: '2022',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  const partnerships = [
    {
      name: 'Mayo Clinic Network',
      description: 'Collaborative care partnership',
      logo: 'https://public.readdy.ai/ai/img_res/b57ba0f0b823d3631608d10f8d2db727.jpg'
    },
    {
      name: 'Johns Hopkins Medicine',
      description: 'Research and education alliance',
      logo: 'https://readdy.ai/api/search-image?query=Medical%20university%20logo%20design%2C%20healthcare%20education%20symbol%2C%20academic%20medical%20institution%20branding%2C%20professional%20healthcare%20logo%2C%20research%20partnership%20identity&width=200&height=100&seq=johns-logo&orientation=landscape'
    },
    {
      name: 'Cleveland Clinic',
      description: 'Clinical excellence partnership',
      logo: 'https://readdy.ai/api/search-image?query=Medical%20clinic%20logo%20design%2C%20healthcare%20excellence%20symbol%2C%20professional%20medical%20branding%2C%20clinical%20partnership%20identity%2C%20healthcare%20quality%20logo&width=200&height=100&seq=cleveland-logo&orientation=landscape'
    },
    {
      name: 'American Heart Association',
      description: 'Cardiovascular care certification',
      logo: 'https://readdy.ai/api/search-image?query=Heart%20health%20organization%20logo%2C%20cardiovascular%20care%20symbol%2C%20medical%20association%20branding%2C%20heart%20disease%20prevention%20logo%2C%20healthcare%20certification%20identity&width=200&height=100&seq=heart-logo&orientation=landscape'
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Certifications */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Certifications & Accreditations</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our commitment to excellence is validated by prestigious certifications 
              and accreditations from leading healthcare organizations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {certifications.map((cert, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 ${cert.bgColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <i className={`${cert.icon} text-xl ${cert.color}`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold text-gray-900">{cert.name}</h3>
                      <span className="text-sm font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {cert.year}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">{cert.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Partnerships */}
        <div>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Strategic Partnerships</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We collaborate with leading medical institutions to bring you access 
              to the latest treatments and medical innovations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {partnerships.map((partner, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105 text-center">
                <div className="mb-6">
                  <img 
                    src={partner.logo}
                    alt={partner.name}
                    className="w-full h-16 object-contain object-center mx-auto"
                  />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{partner.name}</h3>
                <p className="text-gray-600 text-sm">{partner.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-20 bg-gradient-to-r from-teal-600 to-blue-600 rounded-3xl p-8 md:p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">Ready to Experience Excellence?</h3>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Join thousands of patients who trust us with their healthcare needs. 
            Schedule your appointment today and experience the difference quality care makes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Schedule Appointment
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer">
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CertificationsSection;
